import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeftRight, DollarSign, TrendingUp, TrendingDown, Wallet, Building2, ShoppingCart } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';

const COMPARE_CARDS = [
  { key: 'sales', label: 'Sales', icon: DollarSign, color: 'emerald' },
  { key: 'expenses', label: 'Expenses', icon: TrendingDown, color: 'red', invert: true },
  { key: 'profit', label: 'Profit', icon: TrendingUp, color: 'primary' },
  { key: 'transactions', label: 'Transactions', icon: ShoppingCart, color: 'blue' },
  { key: 'cash', label: 'Cash', icon: Wallet, color: 'green' },
  { key: 'bank', label: 'Bank', icon: Building2, color: 'indigo' },
];

/**
 * Foodics-style "Day/Week/Month + Compare toggle" toolbar with comparison cards.
 * Pure presentational: parent owns all state.
 */
export const CompareModeToolbar = ({
  comparePeriod,
  setComparePeriod,
  compareEnabled,
  setCompareEnabled,
  compareData,
  compareLoading,
}) => (
  <Card className="border-0 shadow-sm" data-testid="compare-toggle-card">
    <CardContent className="p-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-muted dark:bg-muted rounded-lg p-1">
          {['day', 'week', 'month'].map(p => (
            <button
              key={p}
              data-testid={`compare-period-${p}`}
              onClick={() => { setComparePeriod(p); if (!compareEnabled) setCompareEnabled(true); }}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all capitalize ${
                comparePeriod === p && compareEnabled
                  ? 'bg-white dark:bg-stone-700 text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p === 'day' ? 'Day' : p === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="compare-switch" className="text-xs font-medium text-muted-foreground">Compare</Label>
          <Switch id="compare-switch" checked={compareEnabled} onCheckedChange={setCompareEnabled} data-testid="compare-switch" />
        </div>
      </div>

      {compareEnabled && compareData && (
        <div className="mt-3 pt-3 border-t border-border dark:border-stone-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ArrowLeftRight size={14} className="text-primary" />
              <span className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground">
                {compareData.labels?.current} vs {compareData.labels?.previous}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {COMPARE_CARDS.map(({ key, label, icon: Icon, color, invert }) => {
              const curr = compareData.current?.[key] ?? 0;
              const prev = compareData.previous?.[key] ?? 0;
              const change = compareData.change?.[key] ?? 0;
              const isPositive = change > 0;
              const isGood = invert ? !isPositive : isPositive;
              return (
                <div key={key} className="bg-muted/50 dark:bg-muted rounded-xl p-3 border border-border dark:border-stone-700" data-testid={`compare-${key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase">{label}</span>
                    <Icon size={13} className={`text-${color}-500`} />
                  </div>
                  <p className="text-base font-bold text-foreground dark:text-stone-100 inline-flex items-center gap-0.5">
                    <RiyalIcon size="0.7em" /> {curr.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 inline-flex items-center gap-0.5">
                    was <RiyalIcon size="0.85em" /> {prev.toLocaleString()}
                  </p>
                  <div className={`mt-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${isGood ? 'bg-success/20 text-success dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-error/20 text-error dark:bg-red-900/30 dark:text-red-400'}`}>
                    {isPositive ? '+' : ''}{change}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {compareEnabled && compareLoading && (
        <div className="mt-3 pt-3 border-t text-center py-4 text-xs text-muted-foreground">Loading comparison...</div>
      )}
    </CardContent>
  </Card>
);
